import {
  Account,
  ConsistencyRuleResult,
  ConsistencyRules,
} from '@monorepo/types';
import { createConsistencyRuleQuery } from '@hub/features/account-dashboard/hooks/query-creators';
import { useStore } from '@hub/helpers/use-store';
import { useQuery } from '@tanstack/react-query';

/**
 * Standardized state for rule query operations
 * Follows SOLID principles: Single Responsibility for query state management
 */
export interface RuleQueryState {
  data: ConsistencyRuleResult | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Custom hook for fetching consistency rule data with standardized error/loading handling
 *
 * Single Responsibility: Handles consistency rule query logic only
 * Open/Closed: Extensible via rule parameter, closed for modification
 * Dependency Inversion: Depends on query creator abstraction
 *
 * @param account - Account to fetch rule for
 * @param rule - Specific consistency rule to fetch
 * @param enabled - Whether query should be enabled (default: true)
 * @returns Standardized query state with data, loading, error, and refetch
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useConsistencyRule(
 *   account,
 *   ConsistencyRules.BestTradeProfitLimit
 * );
 * ```
 */
export const useConsistencyRule = (
  account: Account | undefined,
  rule: ConsistencyRules,
  enabled = true,
): RuleQueryState => {
  const {
    dataStore: { accountStore },
  } = useStore();

  const queryConfig = createConsistencyRuleQuery(accountStore, account, rule);

  const {
    data: ruleData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    ...queryConfig,
    enabled: enabled && (queryConfig.enabled ?? true),
  });

  // Type assertion: BestTradeProfitLimit returns ConsistencyRuleResult
  // Note: This may need adjustment based on actual API return types
  const ruleResult = ruleData as ConsistencyRuleResult | undefined;

  return {
    data: ruleResult,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
